import React, { useEffect, useState } from 'react';
import ProposalReview from './ProposalReview';
import { API } from '../App';

const button = { border: '1px solid var(--border)', borderRadius: 7, padding: '6px 9px', background: 'var(--bg-4)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 11 };

export default function PlanReviewInline({ plan, token, suggestion, messageId, disabled, onSuggestionChange, onApplied, onModify }) {
  const [job, setJob] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState({});

  async function refresh() {
    if (!suggestion.jobId) return;
    const response = await fetch(`${API}/api/plans/${plan.id}/review-all?jobId=${encodeURIComponent(suggestion.jobId)}`, {
      headers: { Authorization: `Bearer ${token}` }, cache: 'no-store',
    });
    if (!response.ok) throw new Error(`Could not load plan review (HTTP ${response.status})`);
    const result = await response.json();
    setJob(result.job);
    setProposals(result.proposals || []);
  }

  useEffect(() => {
    if (suggestion.status !== 'accepted' || !suggestion.jobId) return;
    refresh().catch(err => setError(err.message));
    const timer = setInterval(() => refresh().catch(err => setError(err.message)), 3000);
    return () => clearInterval(timer);
  }, [suggestion.status, suggestion.jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function respond(status) {
    setBusy(true); setError('');
    try {
      let jobId;
      if (status === 'accepted') {
        const started = await fetch(`${API}/api/plans/${plan.id}/review-all`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ instruction: suggestion.decision }),
        });
        const result = await started.json();
        if (!started.ok) throw new Error(result.error || `HTTP ${started.status}`);
        jobId = result.id;
      }
      const response = await fetch(`${API}/api/chat-messages/${messageId}/plan-review`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status, jobId }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || `HTTP ${response.status}`);
      onSuggestionChange(result.planReview);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  async function act(proposal, action) {
    setBusy(true); setError('');
    try {
      const response = await fetch(`${API}/api/ai-proposals/${proposal.id}${action === 'approve' ? '/approve' : ''}`, {
        method: action === 'approve' ? 'POST' : 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      if (!response.ok) throw new Error(`${proposal.path}: ${result.error || `HTTP ${response.status}`}`);
      if (action === 'approve') {
        try { await onApplied?.(result.card); }
        catch { setError('Card saved, but the canvas did not refresh. Reload to see it.'); }
      }
      await refresh();
      return true;
    } catch (err) { setError(err.message); return false; }
    finally { setBusy(false); }
  }

  async function approveSelected() {
    for (const proposal of proposals.filter(item => item.status === 'pending' && selected[item.id])) {
      if (!(await act(proposal, 'approve'))) break;
    }
    setSelected({});
  }

  const pending = proposals.filter(proposal => proposal.status === 'pending');
  const selectedCount = pending.filter(proposal => selected[proposal.id]).length;
  return <section style={{ border: '1px solid #8b5cf680', borderRadius: 10, padding: 10, background: 'var(--bg-3)', fontSize: 12, width: '100%', boxSizing: 'border-box' }}>
    <strong>Review cards across the plan?</strong>
    <p style={{ color: 'var(--text-secondary)', margin: '6px 0' }}>{suggestion.decision}</p>
    {suggestion.status === 'pending' && <>
      <p style={{ color: 'var(--text-secondary)', margin: '6px 0' }}>I’ll check each card’s title and content, then prepare complete updates for likely matches. Nothing changes until you approve them.</p>
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" disabled={busy || disabled} onClick={() => respond('accepted')} style={{ ...button, background: '#6366f1', color: 'white' }}>Yes, review cards</button>
        <button type="button" disabled={busy || disabled} onClick={() => respond('declined')} style={button}>No</button>
      </div>
    </>}
    {suggestion.status === 'declined' && <div style={{ color: 'var(--text-secondary)', marginTop: 6 }}>Okay—no plan-wide scan.</div>}
    {suggestion.status === 'accepted' && <>
      <div style={{ color: 'var(--text-secondary)', marginTop: 7 }}>{job?.status === 'running' ? `Scanning ${job.scanned}/${job.total} cards…` : job ? `${job.status.replaceAll('_', ' ')} · ${job.scanned}/${job.total} scanned · ${job.proposed} proposed` : 'Starting scan…'}{job?.screened_out > 0 && ` · ${job.screened_out} quick-screen skips`}</div>
      {job?.decision_fallback && <div style={{ color: 'var(--text-secondary)', marginTop: 4 }}>Quick screening was unavailable; remaining cards received the full review.</div>}
      {job?.status === 'running' && pending.length > 0 && <div style={{ color: 'var(--text-secondary)', marginTop: 4 }}>You can review and approve these while I scan the remaining cards.</div>}
      {job?.error && <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontFamily: 'inherit', color: '#ef4444' }}>{job.error}</pre>}
      {job?.status === 'completed' && proposals.length === 0 && <p>No updates proposed for this decision.</p>}
      {pending.length > 0 && <div style={{ marginTop: 9 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <strong>{pending.length} proposed update{pending.length === 1 ? '' : 's'}</strong>
          <button type="button" disabled={busy || !selectedCount} onClick={approveSelected} style={button}>Approve selected ({selectedCount})</button>
        </div>
        {pending.map(proposal => <div key={proposal.id} style={{ marginBottom: 10 }}>
          <div style={{ color: 'var(--text-secondary)', fontSize: 11, marginBottom: 5 }}>{proposal.path}</div>
          <label style={{ display: 'block', fontSize: 11, marginBottom: 6 }}><input type="checkbox" checked={!!selected[proposal.id]} onChange={event => setSelected(old => ({ ...old, [proposal.id]: event.target.checked }))} /> Select</label>
          <ProposalReview proposal={{ ...proposal, busy }} onApprove={() => act(proposal, 'approve')} onCancel={() => act(proposal, 'cancel')}
            onModify={async () => { if (await act(proposal, 'cancel')) onModify(proposal); }} />
        </div>)}
      </div>}
    </>}
    {error && <div role="alert" style={{ color: '#ef4444', marginTop: 7 }}>{error}</div>}
  </section>;
}
